/**
 * CountryExposure — TASK-690. The geographic lens Kielis could not show at all.
 *
 * Cross-border and country concentration is a FINMA-relevant view for a Swiss EAM, and nothing
 * in the product rendered it. **No globe** (TASK-681 rejected it): the lens was the gap, the 3D
 * was eye candy, and a table is what an RM can put in a review pack.
 *
 * The honesty that makes this usable in a compliance conversation is the BASIS. Position country
 * data is empty in practice, so most rows resolve from the ISIN prefix — which is the country of
 * REGISTRATION, not of economic exposure. The widget says so, per row and in aggregate, instead
 * of presenting an inferred number as a measured one. Unresolvable positions are `unknown`, never
 * folded into a country.
 */

import { useQuery } from '@tanstack/react-query';

import { httpClient } from '../../../lib/httpClient';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';

interface CountryRow {
  code: string;
  name: string;
  value_chf: number;
  position_count: number;
  bases: string[];
  pct: number | null;
  is_market_designation: boolean;
}

interface ExposureResponse {
  countries: CountryRow[];
  country_count: number;
  truncated: boolean;
  total_value_chf: number;
  position_count: number;
  has_unknown_value: boolean;
  registration_derived_pct: number | null;
}

const BASIS_LABEL: Record<string, string> = {
  declared: 'declared',
  isin_registration: 'by ISIN registration',
  // TASK-895: cash balances carry no ISIN to register but the custodian does state a region.
  // Named apart from `declared` because it is the region column, not the country one.
  declared_region: 'by declared region',
  unknown: 'unresolved',
};

const chf = (value: number) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 })
    .format(value);

export interface CountryExposureProps {
  clientId: string;
  title?: string;
}

export function CountryExposure({ clientId, title }: CountryExposureProps) {
  const query = useQuery({
    queryKey: ['country-exposure', clientId],
    queryFn: () =>
      httpClient.get<ExposureResponse>(`/api/portfolio/${clientId}/country-exposure`),
    enabled: Boolean(clientId),
  });

  const data = query.data;

  return (
    <WidgetContainer title={title ?? 'Exposure by country'} source="portfolio positions">
      {query.isLoading ? (
        <div className="py-8 text-center">
          <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#002B45] border-t-transparent" />
        </div>
      ) : query.isError ? (
        <WidgetErrorState
          title="Couldn't load country exposure"
          detail="The portfolio service didn't respond."
          onRetry={() => void query.refetch()}
        />
      ) : !data?.countries.length ? (
        <p data-testid="exposure-empty" className="py-5 text-center text-sm text-muted-foreground">
          No valued positions to break down by country.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="exposure-table">
              <thead>
                <tr className="border-b border-border text-[11px] text-muted-foreground">
                  <th className="pb-1 font-medium">Country</th>
                  <th className="pb-1 text-right font-medium">Share</th>
                  <th className="pb-1 text-right font-medium">Value</th>
                  <th className="pb-1 text-right font-medium">Positions</th>
                </tr>
              </thead>
              <tbody>
                {data.countries.map((row) => (
                  <tr
                    key={row.code}
                    data-testid={row.code === 'unknown' ? 'exposure-unknown-row' : 'exposure-row'}
                    className="border-b border-border/50 last:border-0"
                  >
                    <td className="py-1.5">
                      <span className="font-medium text-foreground">{row.name}</span>
                      {/* The basis, per row — the difference between a fact and an inference. */}
                      <span className="ml-1.5 text-[10px] text-muted-foreground">
                        {row.bases.map((b) => BASIS_LABEL[b] ?? b).join(' + ')}
                      </span>
                      {row.is_market_designation && (
                        <span
                          data-testid="exposure-market-designation"
                          className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
                        >
                          market, not a country
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full bg-[#002B45]"
                            style={{ width: `${Math.min(100, row.pct ?? 0)}%` }}
                          />
                        </div>
                        <span className="font-mono text-[12px]">
                          {row.pct != null ? `${row.pct}%` : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="py-1.5 text-right font-mono text-[12px]">
                      {chf(row.value_chf)}
                    </td>
                    <td className="py-1.5 text-right font-mono text-[12px]">
                      {row.position_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Aggregate honesty line: how much of this is inferred, and what is missing. */}
          <p data-testid="exposure-basis-note" className="text-[11px] text-muted-foreground">
            {data.country_count} countries over {chf(data.total_value_chf)}.
            {data.registration_derived_pct != null && data.registration_derived_pct > 0 && (
              <>
                {' '}
                {data.registration_derived_pct}% of value is resolved by ISIN registration — where
                the security is registered, not necessarily where the business earns.
              </>
            )}
            {data.has_unknown_value && ' Some positions carry no market value and are excluded from the shares.'}
          </p>
        </div>
      )}
    </WidgetContainer>
  );
}

export default CountryExposure;
